import { useMemo, useCallback, memo, useRef } from "react";
import { motion } from "framer-motion";

import { useMountEffect } from "~/hooks/useMountEffect";

import {
  useSheetModal,
  SHEET_MODAL_DURATION,
  SHEET_MODAL_TRANSITION,
} from "./SheetModal";

import type { ReactNode } from "react";
import type { HTMLMotionProps, PanInfo } from "framer-motion";

export interface SheetModalPanelProps {
  children?: ReactNode;
}

export const SheetModalPanel = memo(function SheetModalPanel({
  children,
}: SheetModalPanelProps) {
  const {
    animationControls,
    backdropControls,
    dragControls,
    size,
    requestClose,
  } = useSheetModal();

  // Use refs to avoid re-rendering
  const elementRef = useRef<HTMLDivElement>(null);
  const dragStartTimeRef = useRef(0);

  const style = {
    height: size === "lg" ? "100%" : undefined,
  };

  const open = useCallback(() => {
    animationControls?.start({
      y: 0,
      transition: {
        ...SHEET_MODAL_TRANSITION,
        bounce: 0.2,
      },
    });
  }, [animationControls]);

  const close = useCallback(() => {
    // Cancel any in progress animations
    animationControls?.stop();

    // Close, but with slightly different settings
    animationControls
      ?.start({
        y: "100%",
        transition: {
          ...SHEET_MODAL_TRANSITION,
          duration: SHEET_MODAL_DURATION * 0.3,
        },
      })
      .then(() => {
        requestClose?.();
      });
  }, [animationControls, requestClose]);

  const handleDragStart = useCallback(() => {
    dragStartTimeRef.current = Date.now();
  }, []);

  const handleDrag = useCallback(
    (event: PointerEvent, info: PanInfo) => {
      const opacity = Math.min(
        1,
        Math.max(
          0.3,
          1 - info.offset.y / (elementRef.current?.clientHeight ?? 1),
        ),
      );

      backdropControls?.set({ opacity });
    },
    [backdropControls],
  );

  const handleDragEnd = useCallback(
    (event: PointerEvent, info: PanInfo) => {
      const height = elementRef.current?.offsetHeight ?? 0;

      // 120px of the sheet showing
      const offsetThreshold = height - 120;

      // Mostly arbitrary calculations to determine if the
      // gesture is a downward swipe
      const isSwipe =
        (info.offset.y > 80 && Date.now() - dragStartTimeRef.current < 80) ||
        info.velocity.y > 800;

      const shouldClose = info.offset.y > offsetThreshold || isSwipe;

      if (shouldClose) {
        close();

        // Start animating the backdrop immediately instead
        // of relying on the AnimatePresence call. This
        // makes the animation feel more connected to the
        // panel itself.
        backdropControls?.start({
          opacity: 0,
          transition: SHEET_MODAL_TRANSITION,
        });
      } else {
        // dragSnapToOrigin occassionally fails. By
        // triggering an addtional, redundant animation we
        // can avoid visual glitches in most cases.
        open();

        // Animate the backdrop back to its initial state
        backdropControls?.start({
          opacity: 1,
          transition: SHEET_MODAL_TRANSITION,
        });
      }

      dragStartTimeRef.current = 0;
    },
    [backdropControls, close, open],
  );

  const motionProps = useMemo<Partial<HTMLMotionProps<"div">>>(() => {
    return {
      drag: "y",
      dragControls,
      dragListener: false,
      dragSnapToOrigin: true,
      dragConstraints: {
        top: 0,
      },
      dragElastic: 0.25,
      dragTransition: {
        bounceStiffness: 350,
      },
      dragPropagation: true,
      initial: { y: "100%" },
      exit: {
        y: "100%",
        transition: SHEET_MODAL_TRANSITION,
      },
      animate: animationControls,
      onDragEnd: handleDragEnd,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
    };
  }, [
    animationControls,
    dragControls,
    handleDrag,
    handleDragEnd,
    handleDragStart,
  ]);

  useMountEffect(() => {
    open();
  });

  return (
    <motion.div
      ref={elementRef}
      className="SheetModalPanel"
      style={style}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
});
